import { FC, useEffect, useState } from "react";
import { usePoolsStore } from "store/poolsStore";
import { useDebounce } from "hooks/useDebounce";
import { fetchPoolsData } from "utils/fetchPoolsData";
import SortingFilterContainer from "./components/SortingFilterContainer/SortingFilterContainer";
import Card from "components/card";
import { TableControlls } from "./components/TableControlls";
import { poolsService } from "services/poolsService";
import ComplexTable from "./components/Tables/ComplexTable";
import { Pagination } from "@mui/material";
import { useNetworksStore } from "store/networksStore";
import { formatValue } from "utils/formatDollarAmount";

type Props = {};

export const Pools: FC<Props> = () => {
  const {
    allPools,
    setAllPools,
    setAllPoolsMeta,
    allPoolsPage,
    allPoolsMeta,
    setAllPoolsPage,

    allPoolsFilters,

    setAllPoolsFilters,
    setMaxFiltersValues,
    resetAllFilters,
  } = usePoolsStore();
  const [isLoading, setIsLoading] = useState(true);
  const [searchValue, setSearchValue] = useState("");
  const debouncedSearchValue = useDebounce(searchValue);
  const { currentNetwork } = useNetworksStore();
  const [totalTvlUsd, setTotalTvlUsd] = useState("");
  const [totalPools, setTotalPools] = useState("");

  useEffect(() => {
    if (!currentNetwork) return;
    setTotalTvlUsd("");
    setTotalPools("");

    poolsService
      .getFiltersLimits(currentNetwork.id)
      .then((filters) => {
        resetAllFilters();

        const capitalization: [number, number] = [
          filters.minTvlUsd,
          Math.ceil(filters.maxTvlUsd),
        ];

        const volume: [number, number] = [
          filters.minVolume24hUsd,
          Math.ceil(filters.maxVolume24hUsd),
        ];
        setMaxFiltersValues({ capitalization, volume });
      })
      .catch((err) => console.error(err));

    poolsService
      .getPoolsStats(currentNetwork.id)
      .then((stats) => {
        setTotalTvlUsd(stats.totalTvlUsd);
        setTotalPools(stats.count);
      })
      .catch((err) => console.error(err));

    return () => setAllPools([]);
  }, [currentNetwork]);

  const fetchUserPools = (page: number, networkId: string) => {
    if (!networkId) return;

    fetchPoolsData({
      page,
      pools: allPools,
      setPools: setAllPools,
      setPoolsMeta: setAllPoolsMeta,
      filters: allPoolsFilters,
      search: debouncedSearchValue,
      networkId,
    }).finally(() => setIsLoading(false));
  };

  useEffect(() => {
    fetchUserPools(1, currentNetwork?.id);
    setIsLoading(true);
  }, [currentNetwork]);

  useEffect(() => {
    if (!allPools?.length) return;

    fetchUserPools(allPoolsPage, currentNetwork?.id);
    setIsLoading(true);
  }, [allPoolsPage]);

  useEffect(() => {
    fetchUserPools(1, currentNetwork?.id);
    setIsLoading(true);
  }, [allPoolsFilters, debouncedSearchValue]);

  return (
    <div className="grid w-full grid-rows-[auto_1fr] gap-[20px] pt-4">
      <SortingFilterContainer
        allPoolsFilters={allPoolsFilters}
        setAllPoolsFilters={setAllPoolsFilters}
      />

      <div>
        <div className="relative">
          <Card
            extra={
              "w-full px-6 pb-6 py-6 sm:overflow-x-auto rounded-[15px] min-h-[674px]"
            }
          >
            <div className="flex items-center gap-6">
              <TableControlls
                searchValue={searchValue}
                setSearchValue={(val) => setSearchValue(val)}
              />
              <p className="font-medium">
                Total pools: <span>{totalPools || "..."}</span>
              </p>
              <p className="font-medium">
                Total TVL:{" "}
                <span>
                  {totalTvlUsd ? formatValue(totalTvlUsd, 2, 2) : "..."}$
                </span>
              </p>
            </div>

            <ComplexTable isLoading={isLoading} tableData={allPools} />

            <Pagination
              page={allPoolsPage}
              variant="outlined"
              shape="rounded"
              count={allPoolsMeta?.totalPages || 0}
              boundaryCount={2}
              siblingCount={0}
              defaultPage={1}
              onChange={(_, page) => setAllPoolsPage(page)}
              sx={{
                "& .MuiPaginationItem-root": {
                  minWidth: { lg: 32, xs: 26 },
                  height: { lg: 32, xs: 26 },
                  mt: "16px",
                  borderColor: "grey",
                  borderRadius: "8px",
                  color: "black",
                  "&.Mui-selected": {
                    backgroundColor: "#0013c1",
                    color: "white",
                  },
                  "&:hover": {
                    backgroundColor: "#0013c1",
                    color: "white",
                  },
                },
                "& .MuiPaginationItem-outlined": {
                  borderColor: "#5763F3",
                },
                "& .MuiPaginationItem-ellipsis": {
                  borderColor: "transparent",
                },
              }}
            />
          </Card>
        </div>
      </div>
    </div>
  );
};
